import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { switchMap } from 'rxjs';
import { ChancenwerkUser } from '../../../master-data-management/users/entities/ChancenwerkUser.js';
import { WikiEntry } from './WikiEntry.js';
import type { Maybe } from '@adornis/base/utilTypes.js';

@Entity()
export class WikiEntryDataviewFilter extends AdornisFilter {
  static override _class = 'WikiEntryDataviewFilter';

  @Field(type => String) readPermission: Maybe<string>;
  @Field(type => String) writePermission: Maybe<string>;

  override getMongoFilter(): Record<string, any> {
    const filter: Record<string, any>[] = [];

    if (this.readPermission) filter.push({ readPermission: this.readPermission });
    if (this.writePermission) filter.push({ writePermission: this.writePermission });

    this.aspects.forEach(aspect => {
      const f = aspect.getMongoFilter();
      if (f) filter.push(f);
    });

    filter.push(super.getMongoFilter());

    return { $and: filter };
  }
}

export const subscribeWikiEntries = registerSubscription({
  type: () => WikiEntry,
  operationName: 'subscribeWikiEntries',
  resolve: (skip: number, limit: number, filter: WikiEntryDataviewFilter) => {
    return (gqlFields: BaseQLSelectionSet<WikiEntry>) => {
      return watchQuery(WikiEntry._collectionName, {}).pipe(
        switchMap(async () => {
          if (context.serverContext) return [];
          const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
            _id: 1,
            roles: AdornisRoleToContextsWrapper.allFields,
          });
          if (!user) return [];
          const permissions = user.roles.reduce((allPermissions, currRole) => {
            allPermissions.push(...currRole.permissions);
            return allPermissions;
          }, [] as string[]);

          const rawPagesCollection = await getRawCollection(WikiEntry._collectionName);
          const result = await rawPagesCollection
            .aggregate<WikiEntry /* & { draft: WikiEntryDraft[]; updatedByUsers: ChancenwerkUser[] }*/>([
              {
                $match: {
                  $or: [
                    {
                      editPermission: null,
                    },
                    {
                      editPermission: {
                        $in: permissions,
                      },
                    },
                  ],
                  ...(filter.search
                    ? {
                        name: new RegExp(filter.search, 'i'),
                      }
                    : {}),
                  ...(filter.readPermission
                    ? {
                        readPermission: filter.readPermission,
                      }
                    : {}),
                  ...(filter.writePermission
                    ? {
                        writePermission: filter.writePermission,
                      }
                    : {}),
                },
              },
              ...(skip ? [{ $skip: skip }] : []),
              ...(limit ? [{ $limit: limit }] : []),
            ])
            .toArray();

          return result;
        }),
      );
    };
  },
  options: {},
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => WikiEntryDataviewFilter },
  ],
});
